import React, {FC, useCallback, useState} from 'react';

import {createGAEvent} from '@optimaxdev/analytics/desktop/lib/gaEvent/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton, Modal, zIndex} from '@optimaxdev/design-system';
import {Icon, EnvelopeClock} from '@optimaxdev/icons';
import {connect} from 'react-redux';

import {TYP_RX_DETAILS, TYP_TYP_FORM_ACTION} from 'constants/analytics/events/typ';
import {ApplicationStoreType} from 'desktop/store';

import {changeSuccessScreen} from '../../store/actions/orderItems/orderItems';
import {
    DONT_FORGET_TO_SEND_US_A_COPY_OF_YOUR_PRESCRIPTION,
    DONT_FORGET_TO_SEND_US_YOUR_DR_DETAILS,
} from '../../store/reducers/orderItems/constants';
import {getSendByMessage, isOnlyDoctorDetails} from '../../store/selectors/orderItems/orderItems';
import {SuccessScreenType} from '../../types/orderItemsTypes';

import s from './sendLaterSuccessButton.scss';

export type PropsType = {
    changeScreen: (screen: SuccessScreenType) => void;
    hasOnlyDoctorDetails: boolean;
    label: string;
};

/**
 * Send later button
 * @returns {React.ReactElement} Rendered react component
 */
export const SendLaterSuccessButton: FC<PropsType> = ({
    changeScreen,
    hasOnlyDoctorDetails,
    label,
}) => {
    const [isVisible, setVisibility] = useState(false);

    const toggleModal = useCallback(() => {
        setVisibility(!isVisible);
        if (label && !isVisible) {
            pushDataLayer({
                ...createGAEvent(TYP_RX_DETAILS, {
                    version: 'D',
                }),
                ...(hasOnlyDoctorDetails
                    ? {eventLabel: 'Skip - Dr. Details'}
                    : {eventLabel: 'Skip - Prescription Image'}),
            });
        }
        if (!isVisible) {
            sendDesktopAnalyticsEvent(TYP_TYP_FORM_ACTION, {label: 'Skip & Send Later'});
        }
    }, [hasOnlyDoctorDetails, isVisible, label]);

    return (
        <>
            <Modal isOpen={isVisible} onRequestClose={toggleModal} zIndex={zIndex.popup}>
                <div className={s.modal}>
                    <h3 className={s.modalTitle}>Just a Quick Heads Up!</h3>
                    <div className={s.iconContainer}>
                        <Icon
                            className={s.icon}
                            icon={EnvelopeClock}
                            role="presentation"
                            strokeWidth={3}
                        />
                    </div>
                    <p className={s.mainText}>
                        We can get your order ready for shipment only <br /> once we receive your
                        prescription.
                    </p>
                    <p className={s.secondText}>
                        The sooner we receive it, the sooner you&apos;ll <br /> get your contacts
                    </p>
                    <CtaButton
                        className={s.cta}
                        colorName="secondary"
                        isFull={false}
                        onClick={toggleModal}
                        size="medium"
                    >
                        Do It Now
                    </CtaButton>
                    <button
                        onClick={() =>
                            changeScreen(
                                hasOnlyDoctorDetails
                                    ? DONT_FORGET_TO_SEND_US_A_COPY_OF_YOUR_PRESCRIPTION
                                    : DONT_FORGET_TO_SEND_US_YOUR_DR_DETAILS,
                            )
                        }
                        className={s.sendLaterModal}
                        type="button"
                    >
                        Send Later
                    </button>
                </div>
            </Modal>
            <div className={s.buttonWrapper}>
                <button className={s.sendLater} onClick={toggleModal} type="button">
                    Skip and Send Later
                </button>
            </div>
        </>
    );
};

const mapStateToProps = (store: ApplicationStoreType) => ({
    hasOnlyDoctorDetails: isOnlyDoctorDetails(store),
    label: getSendByMessage(store),
});

export const SendLaterSuccessButtonConnected = connect(mapStateToProps, {
    changeScreen: changeSuccessScreen,
})(SendLaterSuccessButton);
